import { ZodLiteral, ZodObject, type ZodType, ZodUnion } from 'zod';

/**
 * Collects the `type` literal values accepted by Zod element schemas (objects with a
 * `type: z.literal(...)` field, or (discriminated) unions of such objects, recursively).
 *
 * Use the resulting set for cheap `node.type` lookups in hot paths (e.g. per-keystroke toolbar
 * state), instead of the `ElementApi.is*Element` predicates, whose Zod `safeParse` validates the
 * whole subtree. Does not descend into `children` schemas.
 *
 * @example
 * const LINK_ELEMENT_TYPES = getElementTypes([PlateLink]);
 */
export const getElementTypes = (schemas: ZodType[]): Set<string> => {
    const types = new Set<string>();

    const visit = (node: unknown) => {
        if (node instanceof ZodUnion) {
            node.options.forEach(visit);

            return;
        }

        if (node instanceof ZodObject) {
            const discriminator = node.shape.type;

            if (discriminator instanceof ZodLiteral) {
                discriminator.values.forEach((value) => types.add(String(value)));
            }
        }
    };

    schemas.forEach(visit);

    return types;
};
